// @mui
import { useTheme } from '@mui/material/styles';
// hooks
import useResponsive from '../hooks/useResponsive';

// ----------------------------------------------------------------------

/**
 * Retrieves the font value for a given typography variant, taking into account responsive font sizes.
 *
 * @param {string} variant - The typography variant to retrieve the font value for (e.g. 'h1', 'h2', 'body1').
 * @returns {object} - An object containing the font size, line height, font weight, and letter spacing for the specified variant.
 */
export default function GetFontValue(variant) {
  const theme = useTheme();
  const breakpoints = useWidth();

  const key = theme.breakpoints.up(breakpoints === 'xl' ? 'lg' : breakpoints);

  const hasResponsive =
    variant === 'h1' ||
    variant === 'h2' ||
    variant === 'h3' ||
    variant === 'h4' ||
    variant === 'h5' ||
    variant === 'h6';

  const getFont =
    hasResponsive && theme.typography[variant][key] ? theme.typography[variant][key] : theme.typography[variant];

  const fontSize = remToPx(getFont.fontSize);
  const lineHeight = Number(theme.typography[variant].lineHeight) * fontSize;
  const { fontWeight } = theme.typography[variant];
  const { letterSpacing } = theme.typography[variant];

  return { fontSize, lineHeight, fontWeight, letterSpacing };
}

// ----------------------------------------------------------------------

/**
 * Converts a value in rem units to its equivalent value in pixels.
 *
 * @param {string} value - The value in rem units to be converted.
 * @returns {number} The equivalent value in pixels.
 */
export function remToPx(value) {
  return Math.round(parseFloat(value) * 16);
}

/**
 * Converts a value in pixels to its equivalent value in rem units.
 *
 * @param {number} value - The value in pixels to be converted.
 * @returns {string} The equivalent value in rem units.
 */
export function pxToRem(value) {
  return `${value / 16}rem`;
}

/**
 * Generates responsive font size styles for different screen sizes.
 *
 * @param {Object} options - The options object.
 * @param {number} options.sm - The font size in pixels for small screens.
 * @param {number} options.md - The font size in pixels for medium screens.
 * @param {number} options.lg - The font size in pixels for large screens.
 * @returns {Object} - An object containing the responsive font size styles.
 */
export function responsiveFontSizes({ sm, md, lg }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm)
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md)
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg)
    }
  };
}

// ----------------------------------------------------------------------

function useWidth() {
  const theme = useTheme();
  const keys = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useResponsive('up', key);
      return !output && matches ? key : output;
    }, null) || 'xs'
  );
}
